import React, { useState, useEffect } from "react";
import styles from "./TopFormation.module.sass";
import cn from "classnames";
import Card from "../../../components/Card";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from "recharts";
import useDarkMode from "use-dark-mode";
import { getTopFormations } from "../../../services/dashboard.service";
import { exportTopFormations } from "../../../services/export.service";
import Actions from "../../../components/Actions";
import { toast } from "react-toastify"; 

// Données par défaut pour le chargement initial ou en cas d'erreur
const defaultData = [
    { name: "Chargement...", students: 0 },
    { name: "Chargement...", students: 0 },
    { name: "Chargement...", students: 0 },
    { name: "Chargement...", students: 0 },
    { name: "Chargement...", students: 0 },
    { name: "Chargement...", students: 0 }
];

const TopFormation = ({ className }) => {
    const darkMode = useDarkMode(false);
    const [loading, setLoading] = useState(true);
    const [formations, setFormations] = useState(defaultData);
    const [exporting, setExporting] = useState(false);
    
    useEffect(() => {
        const fetchFormations = async () => { 
            try {
                setLoading(true);
                const data = await getTopFormations(); 
                if (data && data.length > 0) {
                    // Formater les noms de formation pour l'affichage
                    const formattedData = data.map(item => ({
                        ...item,
                        // Stocker le nom complet pour l'infobulle
                        fullName: item.name,
                        // Tronquer les noms trop longs pour l'affichage dans le graphique
                        name: item.name.length > 25 ? `${item.name.substring(0, 22)}...` : item.name
                    }));
                    setFormations(formattedData);
                } else {
                    setFormations([{ name: 'Aucune formation', students: 0 }]); 
                }
            } catch (error) {
                console.error('Erreur lors de la récupération des top formations:', error);
                setFormations([{ name: 'Erreur de chargement', students: 0 }]); 
            } finally {
                setLoading(false);
            }
        };
        
        fetchFormations(); 
    }, []);

    const handleExportFormations = async () => {
        try {
            setExporting(true);
            const result = await exportTopFormations();
            
            if (result && result.success) {
                toast.success(`Export réussi ! Fichier ${result.filename} téléchargé.`);
            } else {
                toast.error('Erreur lors de l\'export des formations');
            }
        } catch (error) {
            console.error('Erreur lors de l\'export des formations:', error);
            toast.error('Erreur lors de l\'export des formations');
        } finally {
            setExporting(false);
        }
    };

    const handleActionSelect = (selectedAction) => {
        console.log("Action sélectionnée :", selectedAction);
        switch (selectedAction) {
            case "Exporter les données":
                handleExportFormations();
                break;
            default:
                console.warn("Action non reconnue :", selectedAction);
                break;
        }
    };

    // Définition des actions pour le composant Actions
    const actions = [
        {
            title: "Exporter les données",
            action: () => handleActionSelect("Exporter les données")
        }
    ];

    return (
        <Card
            className={cn(styles.card, className)}
            title="Top formations"
            classTitle="title-yellow"
            head={
                <Actions
                    className={styles.actions}
                    classActionsHead={styles.actionsHead}
                    items={actions}
                    small
                />
            }
        >
            <div className={styles.chart}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        width={500}
                        height={300}
                        data={loading ? defaultData : formations}
                        layout="vertical"
                        margin={{
                            top: 0,
                            right: 0,
                            left: 5,
                            bottom: 0,
                        }}
                        barSize={24}
                        barGap={8}
                    >
                        <CartesianGrid
                            strokeDasharray="none"
                            stroke={darkMode.value ? "#272B30" : "#EFEFEF"}
                            horizontal={false}
                        />
                        <XAxis
                            type="number"
                            axisLine={false}
                            tickLine={false}
                            tick={{
                                fontSize: 12,
                                fontWeight: "500",
                                fill: "#6F767E",
                            }}
                            padding={{ left: 10 }}
                        />
                        <YAxis
                            type="category"
                            dataKey="name"
                            axisLine={false}
                            tickLine={false}
                            tick={{
                                fontSize: 12,
                                fontWeight: "500",
                                fill: "#6F767E",
                            }}
                        />
                        <Tooltip
                            contentStyle={{
                                backgroundColor: "#272B30",
                                borderColor: "rgba(255, 255, 255, 0.12)",
                                borderRadius: 8,
                                boxShadow:
                                    "0px 4px 8px rgba(0, 0, 0, 0.1), 0px 2px 4px rgba(0, 0, 0, 0.1), inset 0px 0px 1px #000000",
                            }}
                            labelStyle={{
                                fontSize: 12,
                                fontWeight: "500",
                                color: "#fff",
                            }}
                            itemStyle={{
                                padding: 0,
                                textTransform: "capitalize",
                                fontSize: 12,
                                fontWeight: "600",
                                color: "#fff",
                            }}
                            formatter={(value, name, props) => {
                                // Si c'est le nombre d'élèves, afficher avec le libellé
                                if (name === 'students') {
                                    return [`${value} élèves`, 'Inscrits'];
                                }
                                // Sinon, retourner la valeur telle quelle
                                return [value];
                            }}
                            labelFormatter={(label, items) => {
                                // Récupérer l'item correspondant au label
                                const item = formations.find(f => f.name === label);
                                // Afficher le nom complet si disponible, sinon le label tronqué
                                return item?.fullName || label;
                            }}
                            cursor={{ fill: "#f3f2f3" }}
                        />
                        <Bar dataKey="students" fill="#B5E4CA" name="Élèves" />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </Card>
    );
};

export default TopFormation;
